import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { fillTokens, formatDate } from "./legalUtils";

/**
 * Shared layout for every legal / static page.
 * Renders the same nav + footer + typography as the landing page.
 *
 * Props:
 *   slug        - path segment for SEO + breadcrumb (e.g. "privacy-policy")
 *   data        - raw JSON object from content/legal/*.json
 *   crumbLabel  - human breadcrumb label (e.g. "Privacy Policy")
 *   children    - optional custom body (for pages like ContactUs)
 */
const LegalPage = ({ slug, data, crumbLabel, children }) => {
    const filled = React.useMemo(() => fillTokens(data), [data]);
    const path = `/${slug}`;

    return (
        <div className="App" id="top" data-testid={`page-${slug}`}>
            <SEO
                path={path}
                title={filled.title}
                description={filled.description}
            />
            <Nav />
            <main className="pt-24 md:pt-28 pb-4">
                <div className="container-p">
                    <nav
                        className="font-mono text-xs text-mute flex items-center gap-2 flex-wrap"
                        aria-label="Breadcrumb"
                        data-testid="breadcrumb"
                    >
                        <Link
                            to="/"
                            className="hover:text-slate-900 transition-colors"
                        >
                            Home
                        </Link>
                        <span aria-hidden>›</span>
                        <span className="text-slate-700" aria-current="page">
                            {crumbLabel}
                        </span>
                    </nav>

                    <header className="mt-5 max-w-3xl">
                        <h1
                            className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight"
                            data-testid="legal-title"
                        >
                            {filled.title}
                        </h1>
                        {filled.lastUpdated && (
                            <p className="mt-3 font-mono text-xs uppercase tracking-widest text-brand">
                                Last updated · {formatDate(filled.lastUpdated)}
                            </p>
                        )}
                        {filled.intro && (
                            <p className="mt-4 text-dim text-base md:text-lg leading-relaxed">
                                {filled.intro}
                            </p>
                        )}
                    </header>
                </div>
            </main>

            <section className="section pt-6">
                <div className="container-p">
                    <div className="max-w-3xl space-y-8">
                        {children ? children : (filled.sections || []).map((s, i) => (
                            <article
                                key={i}
                                className="panel p-5 md:p-6"
                                data-testid={`section-${i + 1}`}
                            >
                                <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                                    {s.heading}
                                </h2>
                                {Array.isArray(s.body)
                                    ? s.body.map((p, j) => (
                                          <p
                                              key={j}
                                              className="mt-3 text-[15px] md:text-base text-dim leading-relaxed"
                                          >
                                              {p}
                                          </p>
                                      ))
                                    : s.body && (
                                          <p className="mt-3 text-[15px] md:text-base text-dim leading-relaxed">
                                              {s.body}
                                          </p>
                                      )}
                                {s.bullets && s.bullets.length > 0 && (
                                    <ul className="mt-4 space-y-2">
                                        {s.bullets.map((b, k) => (
                                            <li
                                                key={k}
                                                className="flex items-start gap-2.5 text-[15px] text-slate-900 leading-relaxed"
                                            >
                                                <span className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        ))}

                        <div className="pt-2">
                            <Link
                                to="/"
                                className="btn-ghost inline-flex text-sm"
                                data-testid="legal-back"
                            >
                                ← Back to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default LegalPage;
